import { ActionWithPayload } from "@obg/ngrx-utils";
import { updateState } from "@obg/ngx.global";

import { AAMS_REGISTRATION_ACTION_TYPE } from "./aams/aams-registration.action";
import { REGISTRATION_WITH_CEP_ACTION_TYPE } from "./cep/registration-with-cep.action";
import { REGISTRATION_ACTION_TYPE } from "./registration.action";
import {
	RegistrationFormFlow,
	RegistrationPayload,
	RegistrationResponse,
	RegistrationState,
	RegistrationStatusResponse,
} from "./registration.model";
import { REGISTRATION_WITH_SPID_ID_ACTION_TYPE } from "./spid/registration-with-spid.action";

const INITIAL_STATE: RegistrationState = {
	isBusy: false,
};

export function registrationReducer(state = INITIAL_STATE, action: ActionWithPayload): RegistrationState {
	switch (action.type) {
		case AAMS_REGISTRATION_ACTION_TYPE.getRegistrationToken__DO_NOT_USE__: {
			return updateState(state, {
				hashedEmail: undefined,
				token: undefined,
				lastStatus: undefined,
				isBusy: true,
				isSuccessful: false,
				registrationDetails: action.payload,
			});
		}
		case REGISTRATION_ACTION_TYPE.getRegistrationToken:
		case REGISTRATION_ACTION_TYPE.getCPFRegistrationToken:
		case AAMS_REGISTRATION_ACTION_TYPE.getRegistrationToken: {
			return updateState(state, {
				hashedEmail: undefined,
				token: undefined,
				lastStatus: undefined,
				isBusy: true,
				isSuccessful: false,
				registrationDetails: formatRegistrationRequest(action.payload),
			});
		}
		case REGISTRATION_ACTION_TYPE.setRegistrationDetails:
		case AAMS_REGISTRATION_ACTION_TYPE.setRegistrationDetails: {
			return updateState(state, {
				registrationDetails: formatRegistrationRequest(action.payload),
			});
		}
		case REGISTRATION_ACTION_TYPE.setInitialStep: {
			return updateState(state, {
				initialStep: action.payload,
			});
		}
		case REGISTRATION_ACTION_TYPE.clearRegistrationDetails: {
			return updateState(state, {
				registrationDetails: undefined,
			});
		}
		case REGISTRATION_ACTION_TYPE.verifyRegistrationToken: {
			return updateState(state, {
				emailVerificationToken: action.payload,
				isBusy: true,
			});
		}
		case REGISTRATION_ACTION_TYPE.verifyRegistrationTokenSuccess: {
			return updateState(state, {
				token: action.payload,
				isBusy: false,
			});
		}
		case REGISTRATION_ACTION_TYPE.verifyRegistrationTokenFail: {
			return updateState(state, {
				token: undefined,
				hashedEmail: undefined,
			});
		}
		case REGISTRATION_ACTION_TYPE.getRegistrationTokenSuccess:
		case AAMS_REGISTRATION_ACTION_TYPE.getRegistrationTokenSuccess: {
			const response = action.payload as RegistrationResponse;
			return updateState(state, {
				token: response.token,
				hashedEmail: response.hashedEmail,
			});
		}
		case REGISTRATION_ACTION_TYPE.getRegistrationStatusFail: {
			return updateState(state, {
				lastStatus: undefined,
			});
		}
		case REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess: {
			const response = action.payload as RegistrationStatusResponse;
			return updateState(state, {
				lastStatus: response,
			});
		}
		case REGISTRATION_ACTION_TYPE.getAllRegistrationTokens:
		case REGISTRATION_ACTION_TYPE.getRegistrationStatus:
		case REGISTRATION_ACTION_TYPE.getAllRegistrationVerifications: {
			return updateState(state, {
				isBusy: true,
			});
		}
		case REGISTRATION_ACTION_TYPE.getAllRegistrationTokensFail:
		case REGISTRATION_ACTION_TYPE.getAllRegistrationTokensSuccess:
		case REGISTRATION_ACTION_TYPE.getAllRegistrationVerificationsFail:
		case REGISTRATION_ACTION_TYPE.getAllRegistrationVerificationsSuccess:
		case REGISTRATION_ACTION_TYPE.verifyRegistrationTokenFail: {
			return updateState(state, {
				isBusy: false,
			});
		}
		case REGISTRATION_ACTION_TYPE.registrationFailWithSelfExclusion:
		case REGISTRATION_ACTION_TYPE.registrationFail: {
			return updateState(state, {
				isBusy: false,
				isSuccessful: false,
				lastStatus: undefined,
			});
		}
		case REGISTRATION_ACTION_TYPE.registrationSuccess: {
			return updateState(state, {
				isBusy: false,
				isSuccessful: true,
			});
		}
		case REGISTRATION_ACTION_TYPE.resetRegistrationState: {
			return updateState(state, {
				token: undefined,
				hashedEmail: undefined,
				lastStatus: undefined,
				isBusy: false,
				isSuccessful: false,
				initialStep: undefined,
			});
		}
		case REGISTRATION_ACTION_TYPE.getRegistrationTokenFail:
		case REGISTRATION_WITH_CEP_ACTION_TYPE.getCepPersonalDataSuccess:
		case AAMS_REGISTRATION_ACTION_TYPE.getRegistrationTokenFail: {
			return updateState(state, {
				flow: RegistrationFormFlow.Complete,
			});
		}

		case REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getSPIDJwtToken: {
			return updateState(state, {
				isBusy: true,
			});
		}
		case REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getSPIDJwtTokenFail: {
			return updateState(state, {
				isBusy: false,
			});
		}
		case REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationToken: {
			return updateState(state, {
				hashedEmail: undefined,
				token: undefined,
				lastStatus: undefined,
				isBusy: true,
				isSuccessful: false,
			});
		}
		case REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationTokenSuccess: {
			const response = action.payload as RegistrationResponse;

			return updateState(state, {
				token: response.token,
				hashedEmail: response.hashedEmail,
			});
		}

		case REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationTokenFail: {
			return updateState(state, {
				hashedEmail: undefined,
				token: undefined,
				lastStatus: undefined,
				isBusy: false,
				isSuccessful: false,
			});
		}

		default:
			return state;
	}
}

function formatRegistrationRequest(payload: RegistrationPayload): RegistrationPayload {
	if (payload.person && payload.person.dateOfBirth) {
		return {
			...payload,
			person: {
				...payload.person,
				dateOfBirth: new Date(payload.person.dateOfBirth).toISOString(),
			},
		};
	}
	return payload;
}
